import { useState, FormEvent } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Radio, Mail, KeyRound, Wand2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

type AuthMode = "signin" | "signup" | "magic";

export default function Login() {
  const [searchParams] = useSearchParams();
  const initialMode: AuthMode =
    searchParams.get("mode") === "signup" ? "signup"
    : searchParams.get("mode") === "magic" ? "magic"
    : "signin";
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Where to send users after a successful auth (e.g. back to the search
  // results they were on when they clicked Save). Defaults to home.
  const redirectParam = searchParams.get("redirect");
  const safeRedirect =
    redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
      ? redirectParam
      : "/";
  const postAuthOrigin = `${window.location.origin}${safeRedirect}`;

  // Redirect if already logged in
  if (user) {
    navigate(safeRedirect);
    return null;
  }

  const handleEmailPassword = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: postAuthOrigin },
        });
        if (error) throw error;
        toast.success("Check your email to confirm your account!");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome back!");
        navigate(safeRedirect);
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: postAuthOrigin },
      });
      if (error) throw error;
      toast.success("Magic link sent! Check your inbox.");
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: postAuthOrigin,
      });
      if (result.error) {
        toast.error(result.error.message || "Google sign-in failed");
        return;
      }
      if (result.redirected) return;
      toast.success("Welcome!");
      navigate(safeRedirect);
    } catch (err: any) {
      toast.error(err.message || "Google sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col retro-grid-bg">
      <Header compact />
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm">
          {/* Logo + headline */}
          <div className="text-center mb-6">
            <Radio className="w-8 h-8 text-primary mx-auto mb-2" />
            <h1 className="font-retro font-bold text-xl sm:text-2xl">
              {mode === "signup" ? "Create Account" : "Sign In"}
            </h1>
            <p className="font-mono text-xs sm:text-sm text-muted-foreground mt-1">
              {mode === "signup"
                ? "Join the smartest ecommerce listeners."
                : "Welcome back, operator."}
            </p>
          </div>

          {/* Social buttons */}
          <div className="space-y-2 mb-4">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="btn-comic-secondary w-full disabled:opacity-50"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-border" />
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Mode tabs */}
          <div className="flex retro-border mb-0 overflow-hidden">
            <button
              onClick={() => setMode("signin")}
              className={`flex-1 flex items-center justify-center gap-1.5 font-mono text-xs py-3 sm:py-2 min-h-[44px] sm:min-h-0 transition-colors ${
                mode === "signin" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"
              }`}
            >
              <KeyRound className="w-3 h-3" /> Sign In
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 flex items-center justify-center gap-1.5 font-mono text-xs py-3 sm:py-2 min-h-[44px] sm:min-h-0 transition-colors border-l border-r border-border ${
                mode === "signup" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"
              }`}
            >
              <Mail className="w-3 h-3" /> Sign Up
            </button>
            <button
              onClick={() => setMode("magic")}
              className={`flex-1 flex items-center justify-center gap-1.5 font-mono text-xs py-3 sm:py-2 min-h-[44px] sm:min-h-0 transition-colors ${
                mode === "magic" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"
              }`}
            >
              <Wand2 className="w-3 h-3" /> Magic Link
            </button>
          </div>

          {/* Forms */}
          {mode === "magic" ? (
            <form onSubmit={handleMagicLink} className="retro-border border-t-0 retro-shadow bg-card p-5 space-y-4">
              <div>
                <label className="block font-mono text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Email
                </label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-retro"
                  required
                />
              </div>
              <p className="font-mono text-[10px] text-muted-foreground">
                We'll email you a sign-in link. No password needed.
              </p>
              <button
                type="submit"
                disabled={loading}
                className="btn-comic-primary w-full disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Magic Link"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleEmailPassword} className="retro-border border-t-0 retro-shadow bg-card p-5 space-y-4">
              <div>
                <label className="block font-mono text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Email
                </label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-retro"
                  required
                />
              </div>
              <div>
                <label className="block font-mono text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Password
                </label>
                <input
                  type="password"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-retro"
                  required
                  minLength={6}
                />
              </div>
              {mode === "signin" && (
                <Link
                  to="/forgot-password"
                  className="block text-right font-mono text-[10px] text-accent hover:text-retro-teal transition-colors"
                >
                  Forgot password?
                </Link>
              )}
              <button
                type="submit"
                disabled={loading}
                className="btn-comic-primary w-full disabled:opacity-50"
              >
                {loading ? "..." : mode === "signup" ? "Create Account" : "Sign In"}
              </button>
            </form>
          )}

          {/* Footer text */}
          <p className="text-center font-mono text-[10px] text-muted-foreground mt-4">
            {mode === "signup"
              ? "By signing up you agree to our terms."
              : "Save episodes, track topics, get recommendations."}
          </p>
        </div>
      </main>
    </div>
  );
}
